import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useContext, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import globalStore from '../store';
import { DataContext } from '../player/DataContext';
import { leftSideTools } from '../interface/gameController';
var DrawCanvas = forwardRef(function (_, ref) {
    var _a = globalStore.useStoreState(function (store) { return store.playerStore; }), canvasHeight = _a.canvasHeight, canvasWidth = _a.canvasWidth, physicalCanvasScale = _a.physicalCanvasScale;
    var canvasRef = useRef(null);
    var liveCanvasRef = useRef(null);
    // Physical canvas dimensions (smaller for performance)
    // iPad Safari caps canvas at ~16,777,216 total pixels — scale down if needed
    var MAX_CANVAS_AREA = 16777216;
    var rawWidth = Math.round(canvasWidth * physicalCanvasScale);
    var rawHeight = Math.round(canvasHeight * physicalCanvasScale);
    var rawArea = rawWidth * rawHeight;
    var safeScale = rawArea > MAX_CANVAS_AREA ? Math.sqrt(MAX_CANVAS_AREA / rawArea) : 1;
    var physicalWidth = Math.round(rawWidth * safeScale);
    var physicalHeight = Math.round(rawHeight * safeScale);
    var effectiveScale = physicalCanvasScale * safeScale;
    var _b = useContext(DataContext), drawCommands = _b.drawCommands, tool = _b.tool;
    // Only enable pointer events when drawing tools are active
    var isDrawingToolActive = [
        leftSideTools.pen,
        leftSideTools.highlighter,
        leftSideTools.eraser
    ].includes(tool);
    // Note: Live drawing is now handled directly via refs in WhiteBoard for performance
    // Track the last rendered state to avoid unnecessary full redraws
    var lastRenderedCountRef = useRef(0);
    // Cache context refs for performance (avoid repeated getContext calls)
    var mainCtxRef = useRef(null);
    var liveCtxRef = useRef(null);
    // Initialize contexts once
    useEffect(function () {
        if (canvasRef.current && !mainCtxRef.current) {
            mainCtxRef.current = canvasRef.current.getContext('2d');
        }
        if (liveCanvasRef.current && !liveCtxRef.current) {
            liveCtxRef.current = liveCanvasRef.current.getContext('2d');
        }
    }, []);
    // Expose live canvas context and scale to parent for direct drawing
    useImperativeHandle(ref, function () { return ({
        getLiveContext: function () { var _a; return liveCtxRef.current || ((_a = liveCanvasRef.current) === null || _a === void 0 ? void 0 : _a.getContext('2d')) || null; },
        getMainContext: function () { var _a; return mainCtxRef.current || ((_a = canvasRef.current) === null || _a === void 0 ? void 0 : _a.getContext('2d')) || null; },
        getScale: function () { return effectiveScale; },
        clearLiveCanvas: function () {
            var _a;
            var ctx = liveCtxRef.current || ((_a = liveCanvasRef.current) === null || _a === void 0 ? void 0 : _a.getContext('2d'));
            if (ctx) {
                ctx.clearRect(0, 0, physicalWidth, physicalHeight);
            }
        },
    }); }, [physicalWidth, physicalHeight, effectiveScale]);
    // Helper function to draw a single stroke (coordinates are in logical space, scaled to physical)
    var drawStroke = useCallback(function (context, _draw, scale) {
        context.save();
        if (_draw.style === leftSideTools.eraser) {
            context.globalCompositeOperation = 'destination-out';
        }
        else {
            context.globalCompositeOperation = 'source-over';
        }
        context.beginPath();
        context.strokeStyle = _draw.color;
        context.lineWidth = _draw.thickness * scale; // Scale line width too
        context.lineCap = 'round';
        context.lineJoin = 'round';
        _draw.points.forEach(function (_pt, _ind) {
            // Scale logical coordinates to physical canvas
            var x = _pt[0] * scale;
            var y = _pt[1] * scale;
            if (_ind === 0) {
                context.moveTo(x, y);
            }
            else {
                context.lineTo(x, y);
            }
        });
        if (_draw.style === leftSideTools.highlighter) {
            context.globalAlpha = 0.3;
        }
        else if (_draw.style === leftSideTools.eraser) {
            context.strokeStyle = '#FFFFFF';
        }
        context.stroke();
        context.restore();
    }, []);
    // Draw committed strokes - only full redraw when strokes are removed or on eraser
    useEffect(function () {
        var _a, _b;
        var context = mainCtxRef.current || ((_a = canvasRef.current) === null || _a === void 0 ? void 0 : _a.getContext('2d'));
        if (!context)
            return;
        var currentCount = drawCommands.length;
        var lastCount = lastRenderedCountRef.current;
        // Check if we need a full redraw (strokes removed, or last stroke was eraser)
        var needsFullRedraw = currentCount < lastCount ||
            (currentCount > 0 && ((_b = drawCommands[currentCount - 1]) === null || _b === void 0 ? void 0 : _b.style) === leftSideTools.eraser);
        if (needsFullRedraw || currentCount === 0) {
            // Full redraw on physical canvas
            context.clearRect(0, 0, physicalWidth, physicalHeight);
            drawCommands.forEach(function (_draw) { return drawStroke(context, _draw, effectiveScale); });
        }
        else if (currentCount > lastCount) {
            // Incremental draw - only draw new strokes
            for (var i = lastCount; i < currentCount; i++) {
                drawStroke(context, drawCommands[i], effectiveScale);
            }
        }
        lastRenderedCountRef.current = currentCount;
    }, [drawCommands, physicalWidth, physicalHeight, effectiveScale, drawStroke]);
    return (_jsxs(_Fragment, { children: [_jsx("canvas", { style: {
                    width: canvasWidth,
                    height: canvasHeight,
                    pointerEvents: isDrawingToolActive ? 'auto' : 'none'
                }, width: physicalWidth, height: physicalHeight, draggable: false, className: "drawingCanvas", ref: canvasRef, role: "img", "aria-label": "Drawing canvas for freehand sketches and annotations" }), _jsx("canvas", { style: {
                    width: canvasWidth,
                    height: canvasHeight,
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    pointerEvents: 'none',
                }, width: physicalWidth, height: physicalHeight, draggable: false, className: "liveDrawingCanvas", ref: liveCanvasRef, "aria-hidden": "true" })] }));
});
DrawCanvas.displayName = 'DrawCanvas';
export default DrawCanvas;
